import { LockMessage } from '@components/App/Modal/BlockModal/components/PureBlockEditor/components'
import { useTextLossReport } from '@components/App/Modal/BlockModal/hooks'
import { useDebounced } from '@lib/hooks'
import { type EditorValue, RichEditor } from '@octopus-do/octopus-editor'
import type React from 'react'
import { type CSSProperties, useRef } from 'react'

import { clsJoin } from '@/utils'
import type { StorageItem } from '../../types'
import { TitleInput } from '../TitleInput'
import css from './styles.module.css'

// Long enough for the editor to settle after a change before the DOM is
// compared with the value.
const TEXT_LOSS_CHECK_DELAY = 1500

interface Props {
  title: string
  onRename: (newTitle: string) => void
  content: EditorValue
  onChange: (newContent: EditorValue) => void
  onUpload: (files: FileList) => Promise<StorageItem[]>
  onFocus: () => void
  onBlur: () => void
  color: string
  locked: boolean
  readonly: boolean
}

export const PureBlockEditor: React.FC<Props> = ({
  title,
  onChange,
  content,
  onRename,
  color,
  onUpload,
  onFocus,
  onBlur,
  readonly,
  locked,
}) => {
  const style = { '--block-color': color } as CSSProperties

  const disabled = readonly || locked

  const showLock = !readonly && locked

  const wrapperRef = useRef<HTMLDivElement>(null)
  const lastValue = useRef<EditorValue>(content)
  const reportTextLoss = useTextLossReport(wrapperRef)
  const reportTextLossSettled = useDebounced(
    reportTextLoss,
    TEXT_LOSS_CHECK_DELAY
  )

  const handleChange = (value: EditorValue) => {
    lastValue.current = value
    reportTextLossSettled(value)
    onChange(value)
  }

  // Typing that never reaches the value produces no change at all, so the
  // last known value is checked once more when the focus leaves the editor.
  const handleBlur = () => {
    reportTextLoss(lastValue.current)
    onBlur()
  }

  return (
    <div
      ref={wrapperRef}
      onFocus={onFocus}
      onBlur={handleBlur}
      style={style}
      className={clsJoin(css.editor_wrapper, showLock && css.locked)}
    >
      <div className={css.header}>
        <div className={css.input}>
          <TitleInput readonly={disabled} value={title} onChange={onRename} />
        </div>
      </div>
      {showLock && <LockMessage />}
      <RichEditor
        readonly={disabled}
        onChange={handleChange}
        onFilesUpload={onUpload}
        value={content}
        placeholder="Add content..."
      />
    </div>
  )
}
